<template>
  <ai-drawer
    v-model="open"
    :title="
      // eslint-disable-next-line vue/no-deprecated-filter
      room?.label as string | undefined
    "
    @popin-close="popinClose">
    <div class="AiRoomDetails">
      <div class="AiRoomDetails-carousel">
        <ai-carousel
          v-if="carouselItems.length"
          v-model="carouselStep"
          :items="carouselItems"
          hide-gallery />
      </div>

      <div class="AiRoomDetails-carousel-actions">
        <ai-button
          v-if="room?.spacialViewLink"
          :url="room?.spacialViewLink"
          target="_blank"
          icon-left="360-view"
          :label="$t('booking.rooms.360View')"
          class="AiRoomDetails-carouselAction"
          variant="tertiary"
          as-link />

        <ai-button
          v-if="carouselItems.length > 1"
          ref="openGalleryButton"
          icon-left="enlarge"
          :label="$t('booking.rooms.gallery')"
          variant="tertiary"
          :aria-label="$t('accessibility.description.buttonViewGallery')"
          @click="galleryOpened = true" />
      </div>

      <ai-typo
        v-if="room?.label"
        as="h2"
        variant="heading-02"
        class="AiRoomDetails-title">
        {{ room?.label }}
      </ai-typo>

      <ai-typo
        v-if="room?.description"
        as="p"
        variant="paragraph-03-regular"
        class="AiRoomDetails-description">
        {{ room?.description }}
      </ai-typo>

      <div
        v-if="topAmenities.length > 0"
        class="AiRoomDetails-topAmenities"
        role="table">
        <div
          v-for="topAmenity in topAmenities"
          :key="topAmenity.key"
          class="AiRoomDetails-topAmenity"
          role="row">
          <ai-icon
            :name="topAmenity.icon as GenericIconName"
            :namespace="topAmenity.iconNamespace"
            :size="24"
            class="AiRoomDetails-topAmenity-icon" />
          <ai-typo as="span" variant="paragraph-04-regular" role="cell">
            {{ topAmenity.label }}
          </ai-typo>
        </div>
      </div>
    </div>

    <!-- Other amenities -->
    <div class="AiRoomDetails-otherAmenities">
      <ai-typo as="h3" variant="heading-03">
        {{ $t('booking.rooms.details.additionalAmenities') }}
      </ai-typo>

      <div class="AiRoomDetails-otherAmenities-wrapper">
        <ul
          v-for="{ amenityId, facilities, amenityLabel } in otherAmenities"
          :key="amenityId"
          class="AiRoomDetails-otherAmenity">
          <ai-typo
            as="li"
            variant="paragraph-02-bold"
            class="AiRoomDetails-otherAmenity-label">
            {{ amenityLabel }}
          </ai-typo>

          <li>
            <ul
              v-for="{ facilityId, facilityLabel, labels } in facilities"
              :key="facilityId"
              class="AiRoomDetails-otherAmenity-facility">
              <ai-typo
                as="li"
                variant="paragraph-03-bold"
                class="AiRoomDetails-otherAmenity-facilityLabel">
                {{ facilityLabel }}
              </ai-typo>

              <li
                v-for="label in labels"
                :key="label"
                class="AiRoomDetails-otherAmenity-finalLabel">
                <ai-icon
                  name="check"
                  :size="12"
                  class="AiRoomDetails-otherAmenity-finalIcon" />
                <ai-typo as="span" variant="paragraph-03-regular">
                  {{ label }}
                </ai-typo>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>

    <Teleport to="body">
      <ai-media-gallery
        v-model="galleryOpened"
        v-model:step="carouselStep"
        :items="carouselItems"
        @focus-gallery-button="focusGalleryButton" />
    </Teleport>
  </ai-drawer>
</template>

<script setup lang="ts">
import AiButton from '../../atoms/AiButton/AiButton.vue';
import AiIcon from '../../atoms/AiIcon/AiIcon.vue';
import type { GenericIconName } from '../../atoms/AiIcon/types';
import AiImage from '../../atoms/AiImage/AiImage.vue';
import AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import AiCarousel from '../../molecules/AiCarousel/AiCarousel.vue';
import type { AiCarouselItem } from '../../molecules/AiCarousel/types';
import AiDrawer from '../../molecules/AiDrawer/AiDrawer.vue';
import AiMediaGallery from '../../molecules/AiMediaGallery/AiMediaGallery.vue';

import type { Accommodation } from '~~/domains/graphql';
import { getSortedRoomAmenities } from '~~/domains/hotel';

type Props = {
  modelValue: boolean;
  room?: Accommodation;
  carouselComponent?: Component;
};

type Emits = {
  (event: 'update:model-value', value: boolean): void;
  (event: 'popin-close'): void;
};

const props = withDefaults(defineProps<Props>(), {
  carouselComponent: AiImage,
  room: undefined,
});
const emits = defineEmits<Emits>();

const { t } = useI18n();

const open = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:model-value', value);
  },
});

const carouselStep = ref(1);
const carouselItems = computed(() => {
  if (!Array.isArray(props.room?.medias?.photos)) return [] as AiCarouselItem[];

  const photos = props.room?.medias?.photos || [];

  return photos.map(photo => {
    const item = {
      component: props.carouselComponent,
      key: `room-details-${photo?.id}`,
      props: { image: photo, room: props.room } as Record<string, unknown>,
    };

    if (props.carouselComponent === AiImage) {
      item.props = photo as Record<string, unknown>;
    }

    return item;
  }) as AiCarouselItem[];
});

const galleryOpened = ref(false);
const openGalleryButton = ref<ComponentPublicInstance | undefined>();

const topAmenities = computed(() => getSortedRoomAmenities(props.room, t));

const otherAmenities = computed(() => {
  const amenities = props.room?.amenities || [];
  const collection = [];

  for (const amenity of amenities) {
    const category = {
      amenityId: amenity?.name as string,
      amenityLabel: amenity?.label,
      facilities: [] as {
        facilityId: string;
        facilityLabel: string;
        labels: string[];
      }[],
    };

    for (const facility of amenity?.facilities || []) {
      category.facilities.push({
        facilityId: facility?.name as string,
        facilityLabel: facility?.label as string,
        labels: facility?.items?.map(item => item?.name) as string[],
      });
    }

    collection.push(category);
  }

  return collection;
});

const focusGalleryButton = () => {
  openGalleryButton.value?.$el?.focus();
};

const popinClose = () => {
  emits('popin-close');
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/themes/rixos/constants' as RixosTheme;

.AiRoomDetails {
  padding: constants.$padding-03;
  max-width: 100%;
}

.AiRoomDetails-carousel {
  margin-bottom: constants.$margin-02;

  @media (mq.$from-medium) {
    padding-right: constants.$padding-03;
  }
}

.AiRoomDetails-carousel-actions {
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-04;
  }
}

.AiRoomDetails-carouselAction {
  margin-right: constants.$margin-03;
}

.AiRoomDetails-title {
  margin-bottom: constants.$margin-01;
}

.AiRoomDetails-description {
  margin-bottom: constants.$margin-02;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-03;
  }
}

.AiRoomDetails-topAmenities {
  display: flex;
  flex-direction: column;
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: constants.$margin-04;
  }
}

.AiRoomDetails-topAmenity {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: constants.$inner-02;

  @media (mq.$from-medium) {
    margin-bottom: 0;

    &:not(:last-of-type)::after {
      content: '';
      height: 2px;
      width: 2px;
      border-radius: 100%;
      background-color: colors.$stratos-600;
      margin-left: constants.$inner-04;
      margin-right: constants.$inner-04;
    }
  }
}

.AiRoomDetails-topAmenity-icon {
  margin-right: constants.$inner-02;
}

.AiRoomDetails-otherAmenities {
  background-color: colors.$gold-100;
  padding: constants.$padding-03;
}

.AiRoomDetails-otherAmenities-wrapper {
  margin-top: constants.$margin-03;
  display: flex;
  flex-direction: column;

  @media (mq.$from-medium) {
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: constants.$inner-02;
  }
}

.AiRoomDetails-otherAmenity {
  display: inline-block;
  flex-basis: 100%;
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    flex-basis: calc(50% - (constants.$inner-02 / 2));
    margin-bottom: 0;
  }
}

.AiRoomDetails-otherAmenity-label {
  margin-bottom: constants.$inner-03;
}

.AiRoomDetails-otherAmenity-facilityLabel {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: constants.$inner-02;

  &::before {
    content: '';
    display: inline-block;
    height: 4px;
    width: 4px;
    border-radius: 100%;
    background-color: colors.$stratos-900;
    margin-left: constants.$inner-02;
    margin-right: constants.$inner-02;
  }
}

.AiRoomDetails-otherAmenity-finalLabel {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin-bottom: constants.$inner-02;
}

.AiRoomDetails-otherAmenity-finalIcon {
  margin-right: constants.$inner-02;
}

.theme.rixos .AiRoomDetails-otherAmenities {
  background: RixosTheme.$color-banner-gray;
}
</style>
